import type maplibregl from "maplibre-gl";
import { RasterTileSource } from "maplibre-gl";

// Import GeoEntity for use in type guards
import type { GeoEntity } from './contexts/SelectionContext';

// types.ts
export namespace MapLibre {
  export type BackgroundLayer = maplibregl.BackgroundLayerSpecification;
  export type FillLayer = maplibregl.FillLayerSpecification;
  export type LineLayer = maplibregl.LineLayerSpecification;
  export type SymbolLayer = maplibregl.SymbolLayerSpecification;
  export type RasterLayer = maplibregl.RasterLayerSpecification;
  export type CircleLayer = maplibregl.CircleLayerSpecification;
  export type FillExtrusionLayer = maplibregl.FillExtrusionLayerSpecification;
  export type HeatmapLayer = maplibregl.HeatmapLayerSpecification;
  export type HillshadeLayer = maplibregl.HillshadeLayerSpecification;
}
type InlineSource = {
  type: "geojson";
  data: any;
};

export type ExtendedFillLayer = Omit<MapLibre.FillLayer, "source"> & {
  source: string | InlineSource;
};

export type ExtendedLineLayer = Omit<MapLibre.LineLayer, "source"> & {
  source: string | InlineSource;
};

export type ExtendedSymbolLayer = Omit<MapLibre.SymbolLayer, "source"> & {
  source: string | InlineSource;
};

export type ExtendedRasterLayer = Omit<MapLibre.RasterLayer, "source"> & {
  source: string | RasterTileSource;
};

export type MapLibreLayerSpecification =
  | ExtendedFillLayer
  | ExtendedLineLayer
  | ExtendedSymbolLayer
  | ExtendedRasterLayer;

interface BaseLayerConfig {
  id: string;
  type: string;
  url: string;
  useBasicAuth?: boolean;
  useOAuth?: boolean;
  initialVisibility: boolean;
  disableClicking?: boolean;
  disablePopups?: boolean;
  showFiltersOnClick?: boolean;
  searchable?: boolean;
  searchableFields?: string[];
  searchApiEndpoint?: string;
  queryParams?: { [key: string]: string };
  joinedData?: {
    name: string;
    url: string;
    templateFields: {
      parentField: string;
      parentTargetTable?: string;
    };
    enableFilters?: boolean;
    filters?: {
      [key: string]: string;
    };
  }[];
  entityMapping?: {
    table: string;
  };
  error?: boolean;
}

// Search result interface for the map search functionality
export interface SearchResult {
  id: string | number;
  name: string;
  type: string;  // 'coordinate', 'place', etc. - matches layer ID 
  coordinates?: [number, number]; // [lng, lat]
  properties?: Record<string, any>;
  layerId?: string;  // Reference to the layer it belongs to
}

export interface RasterDemLayerConfig extends BaseLayerConfig {
  type: "raster-dem";
  options: {
    minZoom: number;
    maxZoom: number;
    tileSize: number;
    exaggeration: number;
  };
}

export interface GeoJsonLayerConfig extends BaseLayerConfig {
  type: "geojson";
  geometry: "circle" | "line" | "fill" | "symbol"; // Specifies how the GeoJSON data should be rendered
  options?: {
    minZoom?: number;
    maxZoom?: number;
  };
  defaultIcon: string; // Only required if geometry === "symbol"
  styleByField?: string; // Field to use to apply colour ramps
  filters?: {
    type: string;
    db_table: string;
    field: string;
  }[];
  icons?: string[]; // Only required if geometry === "symbol"
  popupFields?: string[]; // Fields to show in popup
  descriptionField?: string; // Field to use for description
  style: {
    // Specific to line type layers
    "line-color"?: string;
    "line-width"?: number;
    // Specific to circle type layers
    "circle-radius"?: number;
    "circle-color"?: string;
    "circle-stroke-width"?: number;
    "circle-stroke-color"?: string;
    // For labels
    labelField?: string;
    // Label properties can now be either static values or zoom-dependent expressions
    // e.g., ["step", ["zoom"], 0, 10, 12, 14, 16] - invisible at zoom < 10, size 12 at zoom 10-14, size 16 at zoom > 14
    labelSize?: number | any[];
    labelColor?: string | any[];
    labelFont?: string[] | any[];
    labelOffset?: [number, number] | any[];
    labelOpacity?: number | any[]; // Control label opacity based on zoom
    labelHaloColor?: string | any[]; // Text outline color
    labelHaloWidth?: number | any[]; // Text outline width
    iconSize?: number; // Only required if geometry === "symbol
    iconStyle?: any;
    opacity?: number;
  };
}

export interface RasterLayerConfig extends BaseLayerConfig {
  type: "raster";
  options: {
    tileSize: number;
    opacity: number;
  };
}

// After RasterLayerConfig, add a new interface for Fulcrum layers
export interface FulcrumLayerConfig extends Omit<GeoJsonLayerConfig, 'type'> {
  type: "fulcrum";
  formId: string;
  region?: "US" | "AU" | "EU";
}

// Update the MapLayerConfig type to include FulcrumLayerConfig
export type MapLayerConfig =
  | RasterDemLayerConfig
  | GeoJsonLayerConfig
  | RasterLayerConfig
  | FulcrumLayerConfig;
  
// Update LegendLayerConfig to include FulcrumLayerConfig too
export type LegendLayerConfig = GeoJsonLayerConfig | RasterLayerConfig | FulcrumLayerConfig;

// Data Source Adapter Pattern Types (100% backwards compatible)
export interface FieldMapping {
  id: string | string[];           // Field path(s) for ID
  name: string | string[];         // Field path(s) for name  
  description?: string | string[]; // Field path(s) for description
  thumbnail?: string | string[];
  created_at?: string | string[];
  last_updated_at?: string | string[];
  url?: string | string[];
  media_type?: string | string[];
  geometry?: string | string[];
  coordinates?: string | string[];
  
  // Custom transformation functions
  transformers?: {
    [field: string]: (value: any, originalData: any) => any;
  };
}

export interface StandardizedDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string | null;
  
  // Optional common fields
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;
  
  // Primary image (for entities with photos)
  primary_image?: {
    id: string | number;
    url: string;
    thumbnail: string;
    name?: string;
    description?: string;
  };
  
  // Media-specific
  url?: string;
  media_type?: string;
  original_file?: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };
  
  // Geographic
  geometry?: string | object;
  coordinates?: [number, number];
  
  // Relationships (standardized format)
  relationships?: {
    related_entities?: Record<string, RelatedEntityItem[]>;
    geo_entities?: Record<string, RelatedEntityItem[]>;
    media?: RelatedEntityItem[];
  };
  
  // Fulcrum-specific data (optional)
  fulcrum_data?: {
    form_id: string;
    created_by?: string;
    updated_by?: string;
    form_values: Record<string, any>;
    photos?: any[];
    mapped_fields: Record<string, any>;
    // Store full media objects here for MediaDrawer to access
    fullMediaObjects?: any[];
  };
  
  // Source metadata for debugging
  _source: {
    type: 'django' | 'fulcrum' | 'external';
    originalId: string | number;
    rawData: any;
  };
}

// Adapter function signature - now async for relationship resolution
export type DataSourceAdapter = (
  rawData: any,
  fieldMapping?: FieldMapping,
  apiEndpoints?: ApiEndpoint[]
) => Promise<StandardizedDataItem[]>;

export interface ApiEndpoint {
  name: string;
  tableName: string;
  relationshipKey: string;
  queryParam: string;
  returnedRelationshipsKey: string;
  api_endpoint: string;
  description: string;
  entityType?: "geo" | "media" | "related";
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  formId?: string; // Fulcrum form ID for Fulcrum data sources
  mapLayerId?: string; // ID of the corresponding map layer (if different from tableName)
  
  // New optional fields for data source adapter pattern (100% backwards compatible)
  dataSourceType?: 'django' | 'fulcrum' | 'external'; // defaults to 'django' if not specified
  isEditable?: boolean; // defaults to true for django, false for external sources
  dataSourceConfig?: {
    fieldMapping?: FieldMapping;
    customTransformers?: Record<string, (value: any, originalData: any) => any>;
  };
  geometryType?: "Point" | "Polygon" | "LineString"; // Type of geometry this endpoint handles
  requiresGeometry?: boolean; // Whether geometry is required for creating new items
  reverseRelationships?: {
    sourceFormId: string;
    sourceFormName: string;
    recordLinkFieldId: string;
    recordLinkFieldLabel: string;
    relationshipKey: string;
    displayName: string;
  }[]; // Configuration for reverse relationships (records that link TO this endpoint)
  
  // Date configuration for timeline/calendar view support
  dateConfig?: {
    hasDateFilter: boolean;
    dateField?: string; // Single date field
    startDateField?: string; // Start date field for date ranges
    endDateField?: string; // End date field for date ranges
    showCalendarByDefault?: boolean; // Auto-show timeline when this endpoint is selected
  };
}

export interface Dashboard {
  name: string;
  url: string;
}

interface MediaUploadConfig {
  service?: "imagekit" | "s3";
}

export interface Config {
  appTitle: string;
  logoPath: string;
  loginImagePath: string;
  loginMessage: string;
  colorScheme: {
    primary: string;
    secondary: string;
  };
  sidebarOpen: boolean;
  primaryFilterLayer: string;
  mapLayers: MapLayerConfig[];
  apiEndpoints: ApiEndpoint[];
  homePageShortcuts: Array<{
    name: string;
    targetTab: number;
    options: { tableName?: string };
  }>;
  homePageTitle: string;
  homePageDescription: string;
  initialMapView: {
    longitude: number;
    latitude: number;
    zoom: number;
    pitch: number;
  };
  dashboardUrl?: string; // Keep for backward compatibility
  dashboardUrls?: Dashboard[]; // Keep for backward compatibility
  toolsUrls?: Dashboard[]; // New preferred naming for external tools
  defaultDataTable?: string;
  showMediaInRelatedItemsCloud?: boolean;
  showGeoInRelatedItemsCloud?: boolean;
  showIndirectRelationshipsInRing?: boolean;
  terrainExaggeration?: number; // Added for 3D terrain control
  backgroundOpacity?: number; // Controls page background overlay opacity (0.0 to 1.0)
  mediaUpload?: MediaUploadConfig;
  enableFulcrumEditing?: boolean; // Global flag to enable editing of Fulcrum data
  hideCardsWithoutImages?: boolean; // Hide cards that don't have primary images in DataGrid
  alwaysRequestRelatedItemsAndMedia?: boolean; // Always load Level 3 (relationships & media) instead of progressive loading
  enableServerSearch?: boolean; // Enable/disable server search functionality
  autoExpandAdditionalDetailsWhenNoImage?: boolean; // Auto-expand Additional Details when items have no primary image or thumbnail
  auth?: {
    useCloudFront: boolean;
    endpoint: string;
    cloudFrontEndpoint: string;
  };
  // Dashboards tab configuration
  dashboardsTab?: {
    enabled?: boolean; // Default true for backward compatibility
    label?: string; // Default "Tools" 
  };
}

// Base interface for all items
export interface BaseDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string;
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;
  relationships?: {
    related_entities?: Record<string, RelatedEntityItem[]>;
    geo_entities?: Record<string, RelatedEntityItem[]>;
    media?: RelatedEntityItem[];
  };
}

// Base interface for URLs that might be presigned
interface PresignedUrlInfo {
  presigned?: boolean;
  presigned_expiry?: number; // Unix timestamp
}

// Media specific fields
export interface MediaItem extends BaseDataItem, PresignedUrlInfo {
  url: string;
  thumbnail: string;
  media_type: string;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  // Remote service details (without presigned info)
  remote_details?: {
    service: "imagekit" | "s3";
    url: string; // Original, non-presigned URL
    thumbnail: string; // Original, non-presigned URL
    id: string;
    mime: string;
    bucket?: string; // For S3
    key?: string; // For S3
  };
}

// Primary image interface used by other entities
export interface PrimaryImage extends PresignedUrlInfo {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  url: string | null;
  thumbnail: string | null;
  name?: string;
  description?: string;
}

// Specific types (exported)
export interface EntityItem extends BaseDataItem {
  primary_image?: {
    url?: string | null;
    thumbnail?: string | null;
  };
}

export interface GeoItem extends EntityItem {
  geometry: string;
}

// The main union type for use in components
export type FlexibleDataItem = EntityItem | MediaItem | GeoItem;

// Helper functions
export function getThumbnail(item: FlexibleDataItem): string {
  // If it's a media item with direct thumbnail
  if ("thumbnail" in item && item.thumbnail) {
    return item.thumbnail;
  }

  // If it has primary_image
  if ("primary_image" in item && item.primary_image?.thumbnail) {
    return item.primary_image.thumbnail;
  }

  // Default placeholder
  return "/icons/placeholder.svg";
}

export function getUrl(item: FlexibleDataItem): string | null {
  if ("url" in item) {
    return item.url;
  }
  if (item.primary_image?.url !== undefined) {
    return item.primary_image.url;
  }
  return null;
}

export interface HomePageShortcut {
  name: string;
  targetTab: number;
}

export interface TaggedItem {
  id: number;
  name: string;
  description?: string;
  is_primary?: boolean;
}

export interface Option {
  id: number;
  name: string;
  description?: string;
  thumbnail?: string;
  is_primary?: boolean;
}

export interface OriginalFileDetails {
  filename: string;
  type: string; // MIME type
  size: string; // in kb
  hashmd5: string;
}

export interface GeoJSONPosition {
  0: number;
  1: number;
  2?: number;
}

export interface GeoJSONPoint {
  type: "Point";
  coordinates: GeoJSONPosition;
}

export interface GeoJSONMultiPoint {
  type: "MultiPoint";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONLineString {
  type: "LineString";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONMultiLineString {
  type: "MultiLineString";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONPolygon {
  type: "Polygon";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONMultiPolygon {
  type: "MultiPolygon";
  coordinates: GeoJSONPosition[][][];
}

export interface GeoJSONGeometryCollection {
  type: "GeometryCollection";
  geometries: GeoJSONGeometry[];
}

export type GeoJSONGeometry =
  | GeoJSONPoint
  | GeoJSONMultiPoint
  | GeoJSONLineString
  | GeoJSONMultiLineString
  | GeoJSONPolygon
  | GeoJSONMultiPolygon
  | GeoJSONGeometryCollection;

export interface GeoJSONFeature {
  type: "Feature";
  geometry: GeoJSONGeometry;
  properties: {
    name: string;
    [key: string]: any;
  };
}

export interface PreviewMapData {
  areas: GeoJSONFeature[];
  points: GeoJSONFeature[];
}

export namespace CultureMapAPI {
  // Get relationship types from config's apiEndpoints
  export type RelationshipType = string; // or more specifically: ApiEndpoint['relationshipKey']

  export interface MediaRelationship {
    id: number;
    type: RelationshipType;
    description: string;
    is_primary: boolean;
  }

  // TODO: Consider renaming this to RelationshipsPostShape across the codebase
  export interface Relationships {
    places?: RelatedEntityItem[];
    family_areas?: RelatedEntityItem[];
    persons?: RelatedEntityItem[];
    collections?: RelatedEntityItem[];
    languages?: RelatedEntityItem[];
    partners?: RelatedEntityItem[];
    plant_animals?: RelatedEntityItem[];
    simple_tags?: RelatedEntityItem[];
    special_things?: RelatedEntityItem[];
    trips?: RelatedEntityItem[];
  }

  export interface RelationshipInput {
    id: number;
    type: string;
    rel_description: string;
    is_primary?: boolean;
  }

  export interface MediaFile {
    id: number;
    url: string;
    name: string;
    type: string;
    remote_details?: RemoteDetails;
  }

  export interface MediaItem {
    id: number;
    name: string;
    description: string;
    user: number;
    organisation?: number;
    created_at: string;
    last_updated_at: string;
    is_primary: boolean | null;
    file: MediaFile;
    relationships?: Relationships;
  }

  // Other API interfaces remain the same
  export interface User {
    id: number;
    username: string;
  }

  interface PrimaryImage {
    id: number;
    url: string;
    name: string;
    description: string;
  }

  export interface BaseItem {
    id: number;
    name: string;
    description: string;
    user: number;
    organisation?: number;
    created_at: string;
    last_updated_at: string;
    primary_image?: PrimaryImage;
    relationships?: {
      [key: string]: RelatedEntityItem[];
    };
  }
}

export interface RawMediaItem {
  id: string;
  name: string;
  description: string;
  file: CultureMapAPI.MediaFile;
  type?: string;
  country_areas?: string[];
  original_filename?: string;
  relationships?: {
    [key: string]: Array<{
      id: number;
      name: string;
      description?: string;
      is_primary?: boolean;
    }>;
  };
}

export interface UploadResult {
  file: File;
  url?: string;
  error?: string;
  uploadType: "imagekit" | "s3";
  remoteDetails?: RemoteDetails;
}

export interface ImageKitConfig {
  useImageKitForImages: boolean;
  useImageKitForVideos: boolean;
  publicKey: string;
  urlEndpoint: string;
}

export type FileProgressStage =
  | "pending"
  | "hashing"
  | "presigning"
  | "uploading"
  | "processing"
  | "server";

export interface FileProgress {
  stage: FileProgressStage;
  completed: number;
  total: number;
  progress: number;
}

export interface UploadProgressBarsProps {
  progress: Record<string, FileProgress>;
  items: MediaItemDetailed[];
}

export interface RemoteDetails {
  service: "imagekit" | "s3";
  url: string;
  thumbnail: string;
  id: string;
  mime: string;
  bucket?: string; // For S3
  key?: string; // For S3
}

export interface RelatedEntityItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name?: string;  // For server inconsistency
  description?: string; // for server inconsistency
  rel_description?: string;
  is_primary?: boolean;
  entity_name?: string;
  entity_description?: string;
  entity_thumbnail?: string | null;
}

export interface RelationshipsResponse {
  related_entities: Record<string, RelatedEntityItem[]>;
  geo_entities: Record<string, RelatedEntityItem[]>;
  media: RelatedEntityItem[];
}

export interface MediaResponse {
  id: number;
  name: string;
  media_type: "photo" | "video" | "pdf" | string;
  url: string;
  thumbnail: string;
  description: string;
  user: number;
  organisation: number;
  created_at: string;
  last_updated_at: string;
  original_file: {
    filename: string;
    size: string;
    type: string;
  };
  remote_details?: {
    service: "imagekit";
    url: string;
    thumbnail: string;
    id: string;
    mime: string;
  };
  source_folder: string | null;
  relationships: RelationshipsResponse;
}

interface RelatedItemsRingProps {
  relationships: {
    related_entities: Record<string, any>;
    geo_entities: {
      place: any[];
      familyarea: any[];
    };
    media: any[];
  };
  onItemClick: (item: any) => void;
}

export interface MediaItemDetailed {
  id: string | number;
  name: string;
  description: string;
  type: string;
  media_type?: string;

  // File information (for upload)
  file?: File;
  fileName?: string;
  previewUrl?: string;

  // URLs (for display)
  url?: string;
  thumbnail?: string;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  // Remote service details (e.g., ImageKit)
  remote_details?: {
    service: "imagekit";
    url: string;
    thumbnail: string;
    id: string;
    mime: string;
  };

  // Update relationships to use BaseRelatedItem for server responses
  relationships: {
    related_entities: {
      [key: string]: RelatedEntityItem[];
    };
    geo_entities: {
      [key: string]: RelatedEntityItem[];
    };
    media: RelatedEntityItem[];
  };

  // Allow dynamic string indexing
  [key: string]: any;
}

// Common fields that both types share
export interface CommonFields {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string;
  relationships?: {
    related_entities?: Record<string, RelatedEntityItem[]>;
    geo_entities?: Record<string, RelatedEntityItem[]>;
    media?: RelatedEntityItem[];
  };
}

// Helper type for components that just need basic display info
export interface DisplayItem extends CommonFields {
  // URL and thumbnail can be either at top level (MediaItem) or in primary_image (DataItem)
  url?: string;
  thumbnail?: string;
  primary_image?: {
    url?: string | null;
    thumbnail?: string | null;
  };
}

// Create a union type for all possible items
export type UnifiedItem = EntityItem | MediaItem | GeoItem;

// Update DataItemDetailsProps
interface DataItemDetailsProps {
  item: FlexibleDataItem;
  onBack: () => void;
  dataDrawerEndpoint: ApiEndpoint | null;  // The endpoint from the drawer
  dataItemEndpoint: ApiEndpoint | null;    // The current item's endpoint
  onItemUpdate?: (updatedItem: FlexibleDataItem) => void;
  onItemDelete?: (itemId: number) => void;
}

// Base minimal fields that are always returned from API
export interface MinimalDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description?: string;
  thumbnail?: string;
}

// For use in contexts/components that only need minimal data
export interface OptionsData {
  [key: string]: MinimalDataItem[];
}

// Helper functions for presigned URLs
export function isPresignedUrl(url: string): boolean {
  return url.includes("X-Amz-Signature=") && url.includes("Expires=");
}

export function getUrlExpiry(url: string): number | null {
  const match = url.match(/Expires=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

// Add to types.ts if not already present
export interface S3Config {
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
}

export interface UploadBatch {
  id: number;
  uuid: string; // Backend returns this but we don't use it for requests
  file_count: number;
  completed_count: number;
  total_size: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  items: Array<{
    id: number;
    name: string;
    status: string;
  }>;
}

export interface OptionsContextType {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  loadOptionsForEndpoint: (endpoints: string | string[]) => Promise<void>;
  isLoading: boolean;
}

// Add this with the other interfaces
export interface UploadError {
  file: string;
  stage: "pre-upload" | "upload" | "post-upload";
  error: string;
}

// Update the existing UploadPhase type
export type UploadPhase =
  | "idle"
  | "importing"
  | "uploading"
  | "complete"
  | "error";

// Add these types
export interface RelatedEntityItemPost {
  id: number;
  description: string;
}

export interface RelationshipsPost {
  related_entities: Record<string, RelatedEntityItemPost[]>;
  geo_entities: Record<string, RelatedEntityItemPost[]>;
  media: RelatedEntityItemPost[];
}

// ============================================================================
// API PAGINATION TYPES
// ============================================================================

// New paginated API response format
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper type for API responses that might be paginated or array
export type ApiResponse<T> = T[] | PaginatedResponse<T>;

// Helper functions for handling API responses
export function extractResultsFromResponse<T>(response: ApiResponse<T>): T[] {
  if (response && typeof response === 'object' && 'results' in response) {
    return response.results || [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
}

export function extractPaginationFromResponse<T>(response: ApiResponse<T>): {
  count: number;
  next: string | null;
  previous: string | null;
} | null {
  if (response && typeof response === 'object' && 'results' in response) {
    return {
      count: response.count,
      next: response.next,
      previous: response.previous
    };
  }
  return null;
}

// ============================================================================
// CENTRALIZED SELECTION SYSTEM TYPES
// ============================================================================

// Re-export selection types from SelectionContext to avoid circular dependencies
export type { GeoEntity, SelectionContextType } from './contexts/SelectionContext';

// Utility types for component props that work with centralized selection
export interface SelectionAwareComponentProps {
  selectedEntity?: GeoEntity | null;
  onSelectionChange?: (entity: GeoEntity | null) => void;
}

// Type guard utilities for centralized selection system
export function isGeoEntityWithMedia(entity: GeoEntity): entity is GeoEntity & { relatedMedia: MediaItemDetailed[] } {
  return entity.relatedMedia !== undefined && entity.relatedMedia.length > 0;
}

export function isGeoEntityWithCoordinates(entity: GeoEntity): entity is GeoEntity & { coordinates: [number, number] } {
  return entity.coordinates !== undefined && entity.coordinates.length === 2;
}

export function isGeoEntityWithGeometry(entity: GeoEntity): entity is GeoEntity & { geometry: any } {
  return entity.geometry !== undefined && entity.geometry !== null;
}

// Entity creation helpers for centralized selection
export interface CreateGeoEntityFromFeatureOptions {
  feature: any;
  layerId: string;
  layerConfig?: any;
  entityType?: string;
}

export interface CreateGeoEntityFromItemOptions {
  item: MediaItem | EntityItem;
  endpoint: ApiEndpoint;
  coordinates?: [number, number];
  geometry?: any;
}

// ============================================================================
